/**
 * Lyrixa share links: the slug, the URL, and the lookup.
 *
 * A shared song is addressed by a short opaque slug rather than by its id —
 * `/share/abc123`, not `/share/3f2a8c91-…`. Two reasons: a UUID in a pasted link
 * is ugly and long enough to get truncated by chat clients, and an id in a public
 * URL leaks the internal key of a private record.
 *
 * The slug is DERIVED from the song id rather than stored, which is the same
 * approach lib/license.ts takes for licence numbers: the same song always produces
 * the same link across reloads without any persistence layer. It also means a link
 * cannot dangle — if the song exists, its slug resolves. `server/src/db`'s adapters
 * derive it with the identical hash, which is what makes a slug minted in this
 * browser resolvable against the real backend for anyone else who opens it.
 *
 * ============================================================================
 * RESOLUTION IS REAL, PROVENANCE IS NOT REVOCABLE. `GET /share/:slug`
 * (server/src/routes/public.ts) reads the real, persisted database and answers for
 * any visitor now, not only the sender's own browser — so a link genuinely reaches
 * `routes/Share.tsx` for whoever opens it, once `shareApi.getShared`'s visibility
 * gate passes. That was not true before this module had a real backend behind it,
 * and `shareUrl` below used to hardcode a production host for exactly that reason:
 * a link that could only ever resolve in the sender's tab was not made more honest
 * by pointing it at a domain that did not exist either.
 *
 * WHAT STILL ISN'T FIXED: the slug remains DERIVED rather than stored, so every
 * song is permanently addressable and "unshare" is only "flip back to private" —
 * there is no way to invalidate one slug while leaving the song shareable under a
 * new one. A server-issued random token, stored with its own revocable row, is the
 * correct long-term design; see docs/api-contract.md.
 * ============================================================================
 */
import { hashString } from './hash';
import { paths } from '../routes/paths';
import type { Song } from '../types/domain';

/** Length of a share slug, in base-36 characters. */
const SLUG_LENGTH = 6;

/**
 * Derives a song's stable share slug, e.g. `abc123`.
 *
 * base36 of a hash below 2^31 is at most 6 characters, so this pads rather than
 * truncates — every slug is exactly SLUG_LENGTH and no two lengths coexist.
 */
export function shareSlug(songId: string): string {
  const h = hashString(`${songId}Lyrixa-share`);
  return h.toString(36).padStart(SLUG_LENGTH, '0').slice(-SLUG_LENGTH);
}

/**
 * The public share URL for a slug.
 *
 * BUILT FROM THE APP'S OWN ORIGIN, not a hardcoded production host. That used to be
 * the right call when nothing behind `/share/:slug` could answer for anyone but the
 * sender's own browser (see the module header) — a real-looking link would have been
 * a worse lie than an obviously fake one. `GET /share/:slug` is now a real endpoint
 * (server/src/routes/public.ts, reading the real database), so `routes/Share.tsx` can
 * genuinely resolve a slug for whoever opens the link. Pointing the link at a domain
 * this app is not actually served from would make it dead again, just for a different
 * reason — so this is `window.location.origin`, the same call config.ts's OAuth
 * redirect and usePlayer's URL-completion already make for the identical reason: a
 * link only works if it names the place that can actually answer it.
 */
export function shareUrl(slug: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}${paths.share(slug)}`;
}

/** The share URL for a song, which is what the Share button copies. */
export function songShareUrl(songId: string): string {
  return shareUrl(shareSlug(songId));
}

/**
 * Finds the song a slug refers to, or null.
 *
 * Compares derived slugs rather than reversing the hash, which is not invertible.
 * Linear over the library: correct, and the library is a handful of songs. The
 * server version is an indexed lookup on a stored token.
 *
 * A LOOKUP, NOT AN ACCESS CHECK, and this distinction is worth stating because the
 * signature does not make it: this resolves ANY song by slug, including a private one.
 * `shareApi.getShared` is the seam that decides whether a slug may be answered — it
 * runs the same lookup and then `isLinkAccessible`, and it is what routes/Share.tsx
 * calls. Nothing in the app calls this directly today.
 *
 * Reach for `getShared` instead. Using this to resolve a visitor's URL would return
 * the song and skip the visibility gate, which is the exact defect the two-field model
 * exists to prevent.
 */
export function findSongBySlug(songs: Song[], slug: string | undefined): Song | null {
  if (!slug) return null;
  const wanted = slug.toLowerCase();
  return songs.find((s) => shareSlug(s.id) === wanted) ?? null;
}
